import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  CircularProgress,
  Box,
  Chip,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  ExitToApp as CheckoutIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { RESIDENT } from '../../config/labels';

/**
 * Reusable Tenant Table Component
 * Used by both Active and Vacated tenant tabs
 */
// Rent status badge for the management table — clickable to open rent history
function RentStatusBadge({ rentStatus, onClick }) {
  if (!rentStatus) return <Typography variant="body2" color="text.secondary">—</Typography>;

  const cfg = {
    PAID:    { label: '✓ Paid',                                          color: 'success' },
    PARTIAL: { label: `₹${rentStatus.due?.toLocaleString('en-IN')} Due`, color: 'warning' },
    PENDING: { label: 'Pending',                                         color: 'default' },
    OVERDUE: { label: `₹${rentStatus.due?.toLocaleString('en-IN')} Due`, color: 'error'   },
    UNKNOWN: { label: 'No rent set',                                     color: 'default' },
  }[rentStatus.rent_status] || { label: rentStatus.rent_status, color: 'default' };

  return (
    <Tooltip title="Click to view rent history">
      <Chip
        label={cfg.label}
        color={cfg.color}
        size="small"
        onClick={onClick}
        sx={{
          fontWeight: 600,
          fontSize: '0.72rem',
          cursor: onClick ? 'pointer' : 'default',
        }}
      />
    </Tooltip>
  );
}

function TenantTable({
  tenants,
  loading,
  readOnly = false,
  showVacatedDate = false,
  showRentStatus = false,
  canEdit = true,
  canDelete = true,
  onView,
  onEdit,
  onCheckout,
  onDelete
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenants || tenants.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 5 }}>
        <Typography variant="h6" color="text.secondary">
          No {RESIDENT.plural.toLowerCase()} found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {readOnly 
            ? `No vacated ${RESIDENT.plural.toLowerCase()} match your search criteria.`
            : `Click "${RESIDENT.add}" to onboard a new ${RESIDENT.singular.toLowerCase()}.`}
        </Typography>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Room</strong></TableCell>
            <TableCell><strong>Cot</strong></TableCell>
            <TableCell><strong>Contact</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Joined</strong></TableCell>
            {showVacatedDate && <TableCell><strong>Vacated</strong></TableCell>}
            {showRentStatus && <TableCell><strong>Rent Status</strong></TableCell>}
            <TableCell align="center"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tenants.map((tenant) => (
            <TableRow key={tenant.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {tenant.name}
                </Typography>
                {tenant.email && (
                  <Typography variant="caption" color="text.secondary">
                    {tenant.email}
                  </Typography>
                )}
              </TableCell>
              
              <TableCell>
                {tenant.room_detail ? (
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {tenant.room_detail.room_name}
                    </Typography>
                    {tenant.branch_name && (
                      <Typography variant="caption" color="text.secondary">
                        {tenant.branch_name}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">Not assigned</Typography>
                )}
              </TableCell>

              <TableCell>
                {tenant.cot_detail ? (
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {tenant.cot_detail.cot_code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tenant.cot_detail.cot_type_display}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </TableCell>
              
              <TableCell>{tenant.phone_number || '-'}</TableCell>

              <TableCell>
                {(() => {
                  const s = tenant.status || (tenant.vacating_date ? 'VACATED' : tenant.joining_date ? 'ACTIVE' : 'PENDING');
                  const cfg = {
                    ACTIVE:  { label: 'Active',  color: 'success' },
                    VACATED: { label: 'Vacated', color: 'default' },
                    PENDING: { label: 'Pending', color: 'warning' },
                  }[s] || { label: s, color: 'default' };
                  return <Chip label={cfg.label} color={cfg.color} size="small" />;
                })()}
              </TableCell>
              
              <TableCell>{formatDate(tenant.joining_date)}</TableCell>
              
              {showVacatedDate && (
                <TableCell>{formatDate(tenant.vacating_date)}</TableCell>
              )}

              {showRentStatus && (
                <TableCell>
                  <RentStatusBadge
                    rentStatus={tenant.current_rent_status}
                    onClick={() => onView?.(tenant)}
                  />
                </TableCell>
              )}

              <TableCell align="center">
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  {/* View button - always available */}
                  <Tooltip title="View Details">
                    <IconButton 
                      size="small" 
                      onClick={() => onView?.(tenant)}
                      color="info"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Edit button - only for non-read-only */}
                  {!readOnly && canEdit && onEdit && (
                    <Tooltip title={`Edit ${RESIDENT.singular}`}>
                      <IconButton 
                        size="small" 
                        onClick={() => onEdit(tenant)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Checkout button - only for active tenants */}
                  {!readOnly && onCheckout && (
                    <Tooltip title={`Checkout ${RESIDENT.singular}`}>
                      <IconButton 
                        size="small" 
                        onClick={() => onCheckout(tenant)}
                        color="warning"
                      >
                        <CheckoutIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Delete button - only for non-read-only */}
                  {!readOnly && canDelete && onDelete && (
                    <Tooltip title={`Delete ${RESIDENT.singular}`}>
                      <IconButton 
                        size="small" 
                        onClick={() => onDelete(tenant)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TenantTable;
